import { BarChartIcon, CarIcon, CheckIcon, DownloadIcon, HomeIcon, UserCogIcon } from 'lucide-react'

import { JourneyStage, JourneyView, useTarget } from '../../journey'

import type { MarginReportState } from './journey'

/**
 * Deals as the Margen report lists them.
 *
 * `total` is what the old single column showed; it is deliberately the exact
 * sum of `vehicle` and `commission`, because the point of the announcement is
 * that the number nobody has to re-check is the same one as before.
 */
const DEALS = [
  {
    plate: 'JWKR·92',
    vehicle: 'Toyota RAV4',
    vehicleMargin: '$ 1.240.000',
    commission: '$ 310.000',
    total: '$ 1.550.000',
  },
  { plate: 'PZBL·45', vehicle: 'Chevrolet Sail', vehicleMargin: '$ 680.000', commission: '$ 0', total: '$ 680.000' },
  {
    plate: 'HXTF·11',
    vehicle: 'Hyundai Tucson',
    vehicleMargin: '$ 1.020.000',
    commission: '$ 245.000',
    total: '$ 1.265.000',
  },
  {
    plate: 'SVLK·17',
    vehicle: 'Kia Sportage',
    vehicleMargin: '$ 890.000',
    commission: '$ 180.000',
    total: '$ 1.070.000',
  },
]

interface ViewProps {
  state: MarginReportState
}

export const Simulator = ({ state }: ViewProps) => (
  <JourneyStage
    state={state}
    user={{ name: 'Marcela', role: 'Administradora' }}
    nav={[
      { label: 'Inicio', icon: HomeIcon },
      { label: 'Reportes', icon: BarChartIcon, target: 'navReportes', active: true },
      { label: 'Vehículos', icon: CarIcon },
      { label: 'Usuarios', icon: UserCogIcon },
    ]}
  >
    <ReportsView state={state} />
    <LoadingView />
    <MarginView state={state} />
  </JourneyStage>
)

const ReportsView = ({ state }: ViewProps) => {
  const tabMargen = useTarget('tabMargen')

  return (
    <JourneyView name="reports" className="flex flex-col bg-white p-3">
      <h3 className="mb-1.5 text-[10px] font-bold text-[#1c1917]">Reportes</h3>

      <div className="mb-2 flex items-end gap-1 border-b border-stone-200">
        <div
          className={`px-2 py-1 text-[8px] ${
            state.tab === 'stock' ? 'border-b-2 border-[#e5484d] font-bold text-[#e5484d]' : 'text-[#706d71]'
          }`}
        >
          Stock
        </div>
        <div className="px-2 py-1 text-[8px] text-[#706d71]">Ventas</div>
        <div
          ref={tabMargen}
          className={`px-2 py-1 text-[8px] ${
            state.tab === 'margen' ? 'border-b-2 border-[#e5484d] font-bold text-[#e5484d]' : 'text-[#706d71]'
          }`}
        >
          Margen
        </div>
      </div>

      <div className="flex flex-1 flex-col space-y-2 opacity-50">
        <div className="grid grid-cols-3 gap-1.5">
          {[
            ['Stock Activo', '38'],
            ['Días Promedio', '54'],
            ['Valorizado', '$612M'],
          ].map(([label, value]) => (
            <div key={label} className="rounded border border-[#e4e1e4] bg-[#faf8f8] p-1.5">
              <p className="font-mono text-[6px] uppercase text-[#706d71]">{label}</p>
              <p className="mt-0.5 text-[10px] font-bold text-[#1c1917]">{value}</p>
            </div>
          ))}
        </div>
        <div className="flex h-20 shrink-0 items-end justify-around rounded border border-[#e4e1e4] px-3 pb-2">
          {['35%', '60%', '45%', '72%', '52%'].map((height, index) => (
            <div key={index} className="w-3 rounded-t bg-stone-300" style={{ height }} />
          ))}
        </div>
      </div>
    </JourneyView>
  )
}

const LoadingView = () => (
  <JourneyView name="loading" className="bg-white p-3">
    <div className="mb-2.5 h-2 w-1/3 animate-pulse rounded bg-stone-200" />
    <div className="space-y-1.5">
      {[100, 92, 80, 100, 66].map((width, index) => (
        <div key={index} className="h-1.5 animate-pulse rounded bg-stone-200" style={{ width: `${width}%` }} />
      ))}
    </div>
  </JourneyView>
)

const MarginView = ({ state }: ViewProps) => {
  const margenHeader = useTarget('margenHeader')
  const exportButton = useTarget('export')

  return (
    <JourneyView name="margin" className="flex flex-col bg-white p-3">
      <div className="mb-2 flex items-end gap-1 border-b border-stone-200">
        <div className="px-2 py-1 text-[8px] text-[#706d71]">Stock</div>
        <div className="px-2 py-1 text-[8px] text-[#706d71]">Ventas</div>
        <div className="border-b-2 border-[#e5484d] px-2 py-1 text-[8px] font-bold text-[#e5484d]">Margen</div>
        <span
          ref={exportButton}
          className="ml-auto mb-1 flex items-center gap-1 rounded bg-[#107c41] px-1.5 py-1 text-[6px] font-bold text-white"
        >
          <DownloadIcon className="h-2 w-2" />
          <span>Excel</span>
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden rounded border border-[#e4e1e4]">
        <div className="flex bg-[#f3f2f1] px-1.5 py-1 font-mono text-[5.5px] font-bold uppercase text-[#706d71]">
          <div className="w-[22%]">Patente</div>
          <div className="flex-1">Vehículo</div>
          <div ref={margenHeader} className="vk-value-swap w-[20%] text-right" data-swapping={state.split}>
            {state.split ? 'Margen Vehículo' : 'Margen'}
          </div>
          <div className="vk-newcol w-[20%] text-right" data-arrived={state.split}>
            Comisión de Crédito
          </div>
          <div className="vk-newcol w-[18%] text-right" data-arrived={state.split}>
            Margen Total
          </div>
        </div>

        {DEALS.map((deal) => (
          <div key={deal.plate} className="flex border-t border-stone-100 px-1.5 py-1 text-[6px]">
            <div className="w-[22%] font-mono">{deal.plate}</div>
            <div className="flex-1">{deal.vehicle}</div>
            <div className="vk-value-swap w-[20%] text-right font-mono" data-swapping={state.split}>
              {state.split ? deal.vehicleMargin : deal.total}
            </div>
            <div
              className="vk-newcol w-[20%] text-right font-mono"
              data-arrived={state.split}
              data-pulse={state.pulseCommission}
            >
              {deal.commission}
            </div>
            <div className="vk-newcol w-[18%] text-right font-mono font-bold" data-arrived={state.split}>
              {deal.total}
            </div>
          </div>
        ))}
      </div>

      <div
        className="mt-1.5 flex items-center justify-end gap-3 rounded border border-[#e4e1e4] bg-[#faf8f8] px-2 py-1 text-[6px] transition-opacity duration-300"
        style={{ opacity: state.totalsVisible ? 1 : 0 }}
      >
        {[
          ['Margen Vehículo', '$ 3.830.000'],
          ['Comisión de Crédito', '$ 735.000'],
          ['Margen Total', '$ 4.565.000'],
        ].map(([label, value], index) => (
          <div key={label} className="text-right">
            <p className="font-mono text-[5px] uppercase text-[#706d71]">{label}</p>
            <p className={`font-mono ${index === 2 ? 'font-bold text-[#107c41]' : 'text-[#1c1917]'}`}>{value}</p>
          </div>
        ))}
      </div>

      <div
        className="absolute bottom-2 left-1/2 z-30 flex -translate-x-1/2 items-center gap-1 rounded bg-[#107c41] px-2.5 py-1 text-[7px] font-bold text-white shadow-md transition-all duration-300"
        style={{
          opacity: state.exportToast ? 1 : 0,
          transform: `translate(-50%, ${state.exportToast ? '0' : '0.75rem'})`,
        }}
      >
        <CheckIcon className="h-2.5 w-2.5" />
        <span>Planilla descargada</span>
      </div>
    </JourneyView>
  )
}
